import { useUserTracking } from '@mono/user-tracking';
import React, { useEffect, useContext, useState } from 'react';

const GeneratePdfContext = React.createContext({ isConnected: false, reportsInProgress: [], generatePdf: (reportId) => null });

const useWebsocketConnection = (url) => {
    const [wsConnection, setWsConnection] = useState();
    const [isConnected, setIsConnected] = useState(false);
    const [connectionTimeout, setConnectionTimeout] = useState(setTimeout(() => false));

    useEffect(() => {
        setWsConnection(new WebSocket(url));
    }, [url]);

    const reconnect = () => {
        clearTimeout(connectionTimeout);
        setConnectionTimeout(
            setTimeout(() => {
                setWsConnection(new WebSocket(url));
            }, 1000)
        );
    };

    useEffect(() => {
        if (!wsConnection) {
            return;
        }
        wsConnection.onopen = () => {
            setIsConnected(true);
        };
        wsConnection.onclose = (e) => {
            console.warn('WS connection closed', e);
            setIsConnected(false);
            if (e.code === 1001) {
                // AWS idle timeout
                reconnect();
            }
        };
        wsConnection.onerror = (e) => {
            console.warn('WS connection error', e);
            setIsConnected(false);
            reconnect();
        };
        return () => {
            clearTimeout(connectionTimeout);
            wsConnection.close();
        };
    }, [wsConnection]);

    return [wsConnection, isConnected];
};

export const GeneratePdfContextProvider = ({ children, wsUrl, metjwt, notification }) => {
    const currentOrganisation = localStorage.getItem('mip-organisation-id');
    const [wsConnection, isConnected] = useWebsocketConnection(`${wsUrl}?token=${metjwt}&organisationId=${currentOrganisation}`);
    const [reportsInProgress, setReportsInProgress] = useState([]);
    const { track } = useUserTracking();

    useEffect(() => {
        if (!isConnected || !wsConnection) {
            return;
        }
        wsConnection.onmessage = onMessageHandler;
    }, [wsConnection, isConnected]);

    const isReportBeingGenerated = (reportId) => {
        return !!reportsInProgress.find((report) => report.reportId === reportId);
    };

    const addReportToProgress = (reportId) => {
        const reportInProgress = {
            reportId,
            percentDone: 0,
        };
        setReportsInProgress((prevReportsInProgress) => [...prevReportsInProgress, reportInProgress]);
    };

    const removeReportFromProgress = (reportId) => {
        setReportsInProgress((prevReportsInProgress) => prevReportsInProgress.filter((someReport) => someReport.reportId !== reportId));
    };

    const reportProgressChanged = ({ reportId, tasksDone, tasksTotal }) => {
        setReportsInProgress((prevReportsInProgress) =>
            prevReportsInProgress.map((report) => {
                if (report.reportId === reportId) {
                    return {
                        ...report,
                        percentDone: (tasksDone / tasksTotal) * 100,
                    };
                } else {
                    return report;
                }
            })
        );
    };

    const onMessageHandler = (event) => {
        const msg = JSON.parse(event.data);
        switch (msg.type) {
            case 'generateReportStarted':
                notification.info({
                    message: 'Processing your request',
                    duration: 2,
                });
                break;
            case 'generateReportSucceeded':
                notification.success({
                    key: msg.reportPdfUrl,
                    message: (
                        <div>
                            <h3>Your report is ready to be downloaded</h3>
                            <a
                                href={msg.reportPdfUrl}
                                target='_blank'
                                onClick={() => {
                                    track('forecast_map_actions', { action: 'site_click_download_pdf' });
                                    notification.close(msg.reportPdfUrl);
                                }}
                                rel='noreferrer'
                            >
                                {msg.reportName}
                            </a>
                        </div>
                    ),
                    duration: 0,
                });
                removeReportFromProgress(msg.reportId);
                break;
            case 'generateReportInProgress':
                const { reportId, tasksDone, tasksTotal } = msg;
                reportProgressChanged({ reportId, tasksDone, tasksTotal });
                break;
            case 'reportInvalidPermission':
            case 'reportIdIsMissing':
            case 'generateReportFailed':
                notification.error({
                    message: msg.type || 'Unknown error',
                    duration: 8,
                });
                console.warn(msg);
                removeReportFromProgress(msg.reportId);
                break;
            default:
                console.warn(`WS message not recognized: ${msg.type}`, event);
        }
    };

    const generatePdf = (reportId) => {
        if (!isConnected || !wsConnection) {
            throw new Error('You cannot request a new PDF. Socket is not connected or there is another request in process already');
        }
        if (isReportBeingGenerated(reportId)) {
            throw new Error('This reports is already being generated');
        }
        const sendPayload = {
            action: 'generateReportPdf',
            reportId,
        };
        wsConnection.send(JSON.stringify(sendPayload));
        addReportToProgress(reportId);
    };

    return (
        <GeneratePdfContext.Provider
            value={{
                isConnected,
                reportsInProgress,
                generatePdf,
            }}
        >
            {children}
        </GeneratePdfContext.Provider>
    );
};

export const useGeneratePdf = (reportId) => {
    const { generatePdf, isConnected, reportsInProgress } = useContext(GeneratePdfContext);
    const reportInProgress = reportsInProgress.find((report) => report.reportId === reportId);

    return {
        generatePdf: () => generatePdf(reportId),
        isConnected,
        inProgress: !!reportInProgress,
        percentDone: reportInProgress ? reportInProgress.percentDone : undefined,
    };
};
